import React, { useEffect, useState } from 'react';
import Menu from 'studio/components/Menu/Menu';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import AppsIcon from '@mui/icons-material/Apps';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SettingsIcon from '@mui/icons-material/Settings';
import CalculateIcon from '@mui/icons-material/Calculate';
import PaletteIcon from '@mui/icons-material/Palette';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CodeIcon from '@mui/icons-material/Code';
import LockIcon from '@mui/icons-material/Lock';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Billing from 'studio/pages/Billing/Billing';
import ResizableDrawer, { DefaultSidebarWidth } from 'studio/components/ResizableDrawer/ResizableDrawer';
import UserManagement from 'studio/pages/UserManagement/UserManagement';
import TenantManagement from 'studio/pages/TenantManagement/TenantManagement';
import Integrations from 'studio/pages/Integrations/Integrations';
import SamlAuthentication from 'studio/pages/SamlAuthentication/SamlAuthentication';
import EmbeddingConfiguration from 'studio/pages/EmbeddingConfiguration/EmbeddingConfiguration';
import AppBar from 'studio/components/AppBar/AppBar';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { darkTheme, lightTheme } from 'studio/themes';
import Progress from 'studio/components/Progress';
import { TenantsIcon, VeezooAdminIcon, VeezooStudioIcon } from 'components/Icons';
import { trackEvent } from 'utils/eventTracking';
import { paths as studioPaths } from 'studio/App/Studio/Studio';
import { ConfirmationModalProvider } from 'studio/components/ConfirmationModal/ConfirmationModal';
import Settings from 'studio/pages/Settings/Settings';
import Embedded from 'studio/pages/Embedded/Embedded';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import RoleManagement from 'studio/pages/RoleManagement/RoleManagement';
import JwtAuthentication from 'studio/pages/JwtAuthentication/JwtAuthentication';
import services from 'services';
import BoardTransfer from 'studio/pages/BoardTransfer/BoardTransfer';
import { Dashboard } from '@mui/icons-material';
import ContextMenuContextProvider from 'components/ContextMenu/ContextMenu';

export const settingsSections = {
  numberFormat: 'number-format',
  changeTheme: 'change-theme',
  modelConfiguration: 'model-configuration',
  queryResultAccess: 'query-result-access'
};

export const paths = {
  root: '/admin',
  settings: '/admin/settings',
  settingsNumberFormat: '/admin/settings/number-format',
  settingsTheme: '/admin/settings/theme',
  settingsModelConfiguration: '/admin/settings/model-configuration',
  settingsLlmAccess: '/admin/settings/query-result-access',
  users: '/admin/users',
  billing: '/admin/billing',
  integrations: '/admin/integrations',
  sso: '/admin/sso',
  ssoSaml: '/admin/sso/saml',
  ssoJwt: '/admin/sso/jwt',
  roles: '/admin/roles',
  embedded: '/admin/embedded',
  embeddedConfiguration: '/admin/embedded/configuration',
  embeddedTenants: '/admin/embedded/tenants',
  boardTransfer: '/admin/board-transfer'
};

const Admin = ({ isDarkTheme, toggleTheme, user, environment, languages, children }) => {
  useEffect(() => {
    trackEvent('Admin Opened');
  }, []);

  const location = useLocation();

  // the primary color
  const primary = '#2e8dd5';
  const themeProps = { primary, width: DefaultSidebarWidth };
  const theme = createTheme(isDarkTheme ? darkTheme(themeProps) : lightTheme(themeProps));

  // specifies whether embedding is allowed for the organization
  const [embeddingIsAllowed, setEmbeddingIsAllowed] = useState(undefined);
  // the promise loading Stripe
  const [stripePromise, setStripePromise] = useState(null);
  // specifies whether the Google Maps script has finished loading
  const [googleMapsScriptHasLoaded, setGoogleMapsScriptHasLoaded] = useState(false);

  const isLoading =
    !user || !environment || !languages || embeddingIsAllowed === undefined || !googleMapsScriptHasLoaded;

  // retrieves the embedding configuration to check if embedding is allowed for the organization
  const getEmbeddingConfiguration = async () => {
    const response = await services.getEmbeddingConfiguration();
    if (!response.success && response.response.status === 402) {
      setEmbeddingIsAllowed(false);
    } else {
      setEmbeddingIsAllowed(true);
    }
  };

  useEffect(() => {
    getEmbeddingConfiguration();
  }, []);

  // loads Stripe
  useEffect(() => {
    if (environment?.stripePublishableApiKey) {
      setStripePromise(loadStripe(environment.stripePublishableApiKey));
    }
  }, [environment?.stripePublishableApiKey]);

  // creates a script tag loading the Google Maps script using the retrieved API key
  useEffect(() => {
    if (environment) {
      const script = document.createElement('script');
      const key = environment.googleMapsApiKey;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
      script.async = true;
      script.onload = () => setGoogleMapsScriptHasLoaded(true);
      document.body.appendChild(script);
    }
  }, [environment]);

  const content = (
    <>
      <AppBar
        title="Admin"
        icon={VeezooAdminIcon}
        href={paths.root}
        isDarkTheme={isDarkTheme}
        toggleTheme={toggleTheme}
        isSingleSignOn={user?.isSingleSignOn}
        user={user}
        additionalButton={{
          title: 'Open Veezoo Studio',
          icon: <VeezooStudioIcon />,
          href: studioPaths.root
        }}
      />
      <ResizableDrawer isResizable={false}>
        <Menu
          items={[
            {
              href: paths.settings,
              title: 'Settings',
              icon: SettingsIcon,
              tooltip: 'Customize Veezoo by configuring global settings',
              items: [
                {
                  href: paths.settingsNumberFormat,
                  title: 'Number Format',
                  icon: CalculateIcon,
                  tooltip: 'Set the default number format'
                },
                {
                  href: paths.settingsTheme,
                  title: 'Change Theme',
                  icon: PaletteIcon,
                  tooltip: 'Configure the theme of Veezoo'
                },
                ...(user?.isAdmin
                  ? [
                      {
                        href: paths.settingsModelConfiguration,
                        title: 'Model Configuration',
                        icon: PsychologyIcon,
                        tooltip: 'Configure AI model for question answering'
                      }
                    ]
                  : []),
                {
                  href: paths.settingsLlmAccess,
                  title: 'Query Result Access',
                  icon: SmartToyIcon,
                  tooltip: 'Configure LLM access to database query results'
                }
              ]
            },
            {
              href: paths.users,
              title: 'Users',
              icon: PeopleIcon,
              tooltip: 'Manage existing users and invite new ones'
            },
            {
              href: paths.boardTransfer,
              title: 'Board Transfer',
              icon: Dashboard,
              tooltip: 'Transfer board ownership between users'
            },
            {
              href: paths.roles,
              title: 'Roles',
              icon: SecurityIcon,
              tooltip: 'Manage user roles and assign users to them'
            },
            {
              href: paths.billing,
              title: 'Billing',
              icon: PaymentIcon,
              tooltip: 'Manage your subscription and billing information'
            },
            {
              href: paths.integrations,
              title: 'APIs & Integrations',
              icon: AppsIcon,
              tooltip: 'Enable and configure third-party integrations and APIs'
            },
            {
              href: paths.sso,
              title: 'Single Sign-On',
              icon: LockIcon,
              tooltip: 'Enable and configure single sign-on authentication using SAML or JWT',
              items: [
                {
                  href: paths.ssoSaml,
                  title: 'SAML',
                  icon: VpnKeyIcon,
                  tooltip: 'Enable and configure single sign-on authentication using SAML'
                },
                {
                  href: paths.ssoJwt,
                  title: 'JWT',
                  icon: VpnKeyIcon,
                  tooltip: 'Enable and configure single sign-on authentication using JWT'
                }
              ]
            },
            {
              href: paths.embedded,
              title: 'Embedded',
              icon: CodeIcon,
              tooltip: 'Configure the embedding of Veezoo within your application and set up multiple tenants',
              items: [
                embeddingIsAllowed
                  ? [
                      {
                        href: paths.embeddedConfiguration,
                        title: 'Configuration',
                        icon: BuildIcon,
                        tooltip: 'Configure the embedding of Veezoo within your application'
                      }
                    ]
                  : [],
                user?.organizationUsesTenants
                  ? [
                      {
                        href: paths.embeddedTenants,
                        title: 'Tenants',
                        icon: TenantsIcon,
                        tooltip: 'Set up and manage multiple tenants to access embedded Veezoo'
                      }
                    ]
                  : []
              ].flat()
            }
          ]}
        />
      </ResizableDrawer>
      <Switch>
        {/* redirect from the root to the settings page */}
        <Redirect exact from={paths.root} to={paths.settings} />
        <Route exact path={paths.settings}>
          <Settings user={user} />
        </Route>
        <Route exact path={paths.settingsNumberFormat}>
          <Settings user={user} initialSection={settingsSections.numberFormat} />
        </Route>
        <Route exact path={paths.settingsTheme}>
          <Settings user={user} initialSection={settingsSections.changeTheme} />
        </Route>
        <Route exact path={paths.settingsModelConfiguration}>
          <Settings user={user} initialSection={settingsSections.modelConfiguration} />
        </Route>
        <Route exact path={paths.settingsLlmAccess}>
          <Settings user={user} initialSection={settingsSections.queryResultAccess} />
        </Route>
        <Route exact path={paths.boardTransfer}>
          <BoardTransfer />
        </Route>
        <Route exact path={paths.users}>
          <UserManagement user={user} />
        </Route>
        <Route exact path={paths.roles}>
          <RoleManagement />
        </Route>
        <Route exact path={paths.billing}>
          <Elements stripe={stripePromise}>
            <Billing user={user} />
          </Elements>
        </Route>
        <Route exact path={paths.integrations}>
          <Integrations />
        </Route>
        <Redirect exact from={paths.sso} to={paths.ssoSaml} />
        <Route exact path={paths.ssoSaml}>
          <SamlAuthentication user={user} languages={languages} />
        </Route>
        <Route exact path={paths.ssoJwt}>
          <JwtAuthentication user={user} languages={languages} />
        </Route>
        {!embeddingIsAllowed && (
          <Route exact path={paths.embedded}>
            <Embedded />
          </Route>
        )}
        {user?.organizationUsesTenants && (
          <Route exact path={paths.embeddedTenants}>
            <TenantManagement />
          </Route>
        )}
        {embeddingIsAllowed && (
          <>
            {/* redirect to the embedded configuration page if embedding is allowed for the organization */}
            <Redirect exact from={paths.embedded} to={paths.embeddedConfiguration} />
            <Route exact path={paths.embeddedConfiguration}>
              <EmbeddingConfiguration />
            </Route>
          </>
        )}
        {/* redirect to the root page for all other paths */}
        <Redirect to={paths.root + location.search} />
      </Switch>
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <ContextMenuContextProvider>
        <ConfirmationModalProvider>
          {children}
          {isLoading ? <Progress /> : content}
        </ConfirmationModalProvider>
      </ContextMenuContextProvider>
    </ThemeProvider>
  );
};

export default Admin;
